import { useState, useEffect, useMemo, useCallback } from 'react';
import { useWebSocket } from '../context/WebSocketContext';
import { fetchMarketData, fetchLivePrices } from '../services/api';

// Default coin metadata (for when API doesn't provide complete data)
const COIN_METADATA = {
  btc: { id: 'bitcoin', name: 'Bitcoin', icon: '₿', color: '#f7931a' },
  eth: { id: 'ethereum', name: 'Ethereum', icon: 'Ξ', color: '#627eea' },
  usdt: { id: 'tether', name: 'Tether', icon: '₮', color: '#26a17b' },
  bnb: { id: 'binancecoin', name: 'BNB', icon: 'B', color: '#f3ba2f' },
  sol: { id: 'solana', name: 'Solana', icon: 'S', color: '#9945ff' },
  xrp: { id: 'ripple', name: 'XRP', icon: 'X', color: '#23292f' },
  usdc: { id: 'usd-coin', name: 'USDC', icon: 'C', color: '#2775ca' },
  ada: { id: 'cardano', name: 'Cardano', icon: 'A', color: '#0033ad' },
  doge: { id: 'dogecoin', name: 'Dogecoin', icon: 'D', color: '#c2a633' },
  trx: { id: 'tron', name: 'TRON', icon: 'T', color: '#ff060a' },
  dot: { id: 'polkadot', name: 'Polkadot', icon: '●', color: '#e6007a' },
  avax: { id: 'avalanche-2', name: 'Avalanche', icon: '◆', color: '#e84142' },
  matic: { id: 'matic-network', name: 'Polygon', icon: '⬡', color: '#8247e5' },
  ltc: { id: 'litecoin', name: 'Litecoin', icon: 'Ł', color: '#bfbbbb' },
  link: { id: 'chainlink', name: 'Chainlink', icon: '⬡', color: '#2a5ada' },
  uni: { id: 'uniswap', name: 'Uniswap', icon: '🦄', color: '#ff007a' },
  atom: { id: 'cosmos', name: 'Cosmos', icon: '⚛', color: '#2e3148' },
  xlm: { id: 'stellar', name: 'Stellar', icon: '★', color: '#08b5e5' },
  xmr: { id: 'monero', name: 'Monero', icon: 'M', color: '#ff6600' },
  algo: { id: 'algorand', name: 'Algorand', icon: 'A', color: '#000000' },
};

// Format currency for display
const formatCurrency = (value) => {
  if (!value && value !== 0) return '$0.00';
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
  return `$${value.toFixed(2)}`;
};

// Format price for display
const formatPrice = (price) => {
  if (!price && price !== 0) return '$0.00';
  if (price < 0.01) return `$${price.toFixed(6)}`;
  if (price < 1) return `$${price.toFixed(4)}`;
  return `$${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Calculate price change direction for sparklines
const getSparklinePath = (change24h) => {
  if (change24h >= 0) {
    // Upward trend
    return "M0,25 Q20,22 35,18 T70,12 T100,5";
  } else {
    // Downward trend
    return "M0,5 Q20,8 35,12 T70,18 T100,25";
  }
};

// Default global stats
const DEFAULT_GLOBAL_STATS = {
  totalMarketCap: 1.72e12,
  totalVolume24h: 48.2e9,
  btcDominance: 49.2,
  fearGreedValue: 72,
  fearGreedLabel: 'Greed',
};

/**
 * Hook for live market data with WebSocket updates
 * @param {Object} options - Configuration options
 * @param {boolean} options.useWebSocket - Whether to use WebSocket for live updates (default: true)
 * @param {number} options.pollingInterval - Polling interval in ms when WebSocket is disabled (default: 30000)
 */
export const useLiveMarketData = (options = {}) => {
  const { useWebSocket: enableWebSocket = true, pollingInterval = 30000 } = options;
  const { connected, marketData: wsMarketData, subscribeToMarket, unsubscribeFromMarket } = useWebSocket();
  
  const [restMarketData, setRestMarketData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(Date.now());

  // Fetch initial data from REST API
  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const [marketResponse, pricesResponse] = await Promise.all([
        fetchMarketData(),
        fetchLivePrices(),
      ]);
      
      setRestMarketData({
        market: marketResponse,
        prices: pricesResponse,
      });
      setLastUpdate(Date.now());
    } catch (err) {
      console.error('Error fetching market data:', err);
      setError(err.message || 'Failed to fetch market data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Subscribe to WebSocket market data
  useEffect(() => {
    if (enableWebSocket) {
      subscribeToMarket();
      return () => unsubscribeFromMarket();
    }
  }, [enableWebSocket, subscribeToMarket, unsubscribeFromMarket]);

  // Fetch initial data on mount
  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Set up polling as fallback when WebSocket is disabled or disconnected
  useEffect(() => {
    if (!enableWebSocket || !connected) {
      const interval = setInterval(() => {
        fetchInitialData();
      }, pollingInterval);
      
      return () => clearInterval(interval);
    }
  }, [enableWebSocket, connected, pollingInterval, fetchInitialData]);

  // Merge REST data with WebSocket updates
  const mergedData = useMemo(() => {
    // Use WebSocket data if available, otherwise fall back to REST data
    const sourceData = (enableWebSocket && wsMarketData) 
      ? wsMarketData 
      : restMarketData?.prices;
    
    if (!sourceData) return null;

    // Process coins data
    const coins = (sourceData.coins || []).map((coin, index) => {
      const meta = COIN_METADATA[coin.symbol] || { 
        icon: coin.symbol?.charAt(0).toUpperCase() || '?',
        color: '#3b82f6'
      };
      
      return {
        id: coin.id || meta.id || index,
        symbol: coin.symbol?.toUpperCase() || '',
        name: coin.name || meta.name || coin.symbol?.toUpperCase() || '',
        icon: meta.icon,
        color: meta.color,
        image: coin.image,
        price: coin.price || 0,
        change24h: coin.change24h || 0,
        volume24h: coin.volume24h || 0,
        marketCap: coin.marketCap || 0,
        marketCapRank: coin.marketCapRank || index + 1,
        sparklinePath: getSparklinePath(coin.change24h || 0),
        formattedPrice: formatPrice(coin.price),
        formattedVolume: formatCurrency(coin.volume24h),
        formattedMarketCap: formatCurrency(coin.marketCap),
      };
    });

    // Process global stats
    const wsStats = sourceData.globalStats;
    const globalStats = wsStats ? {
      marketCap: {
        value: formatCurrency(wsStats.totalMarketCap),
        rawValue: wsStats.totalMarketCap,
        change: calculateGlobalChange(wsStats.totalMarketCap, 0.024),
        label: 'Market Cap',
      },
      volume24h: {
        value: formatCurrency(wsStats.totalVolume24h),
        rawValue: wsStats.totalVolume24h,
        change: calculateGlobalChange(wsStats.totalVolume24h, 0.158),
        label: '24h Volume',
      },
      btcDominance: {
        value: `${wsStats.btcDominance.toFixed(1)}%`,
        rawValue: wsStats.btcDominance,
        change: { value: '-0.3%', isPositive: false },
        label: 'BTC Dominance',
      },
      fearGreed: {
        value: wsStats.fearGreedValue.toString(),
        rawValue: wsStats.fearGreedValue,
        change: { value: wsStats.fearGreedLabel, isPositive: wsStats.fearGreedValue > 50 },
        label: 'Fear & Greed',
      },
    } : getDefaultGlobalStats(restMarketData?.market);

    return {
      coins,
      globalStats,
      lastUpdated: sourceData.lastUpdated || Date.now(),
      isLive: enableWebSocket && connected,
    };
  }, [wsMarketData, restMarketData, enableWebSocket, connected]);

  // Calculate trending coins (top 4 by positive change)
  const trendingCoins = useMemo(() => {
    if (!mergedData?.coins) return [];
    return mergedData.coins
      .filter(c => c.change24h > 0)
      .sort((a, b) => b.change24h - a.change24h)
      .slice(0, 4);
  }, [mergedData]);

  // Get top coins by market cap
  const topCoins = useMemo(() => {
    if (!mergedData?.coins) return [];
    return mergedData.coins
      .sort((a, b) => a.marketCapRank - b.marketCapRank)
      .slice(0, 20);
  }, [mergedData]);

  // Manual refresh function
  const refresh = useCallback(async () => {
    await fetchInitialData();
  }, [fetchInitialData]);

  return {
    // Data
    coins: topCoins,
    trendingCoins,
    globalStats: mergedData?.globalStats || getDefaultGlobalStats(),
    lastUpdated: mergedData?.lastUpdated,
    
    // Status
    isLoading,
    error,
    isLive: mergedData?.isLive || false,
    isConnected: connected,
    
    // Actions
    refresh,
  };
};

// Helper function to calculate global change (simulated based on current value)
function calculateGlobalChange(currentValue, defaultChangePercent) {
  // In a real implementation, you would compare with previous value
  // For now, we use small random variations around the default
  const variation = (Math.random() - 0.5) * 0.02; // ±1% variation
  const changePercent = defaultChangePercent + variation;
  const isPositive = changePercent >= 0;
  
  return {
    value: `${isPositive ? '+' : ''}${(changePercent * 100).toFixed(1)}%`,
    isPositive,
  };
}

// Helper function to get default global stats
function getDefaultGlobalStats(marketData = null) {
  const stats = DEFAULT_GLOBAL_STATS;
  
  // Try to calculate from market data if available
  let totalMarketCap = stats.totalMarketCap;
  let totalVolume = stats.totalVolume24h;
  
  if (marketData && Object.keys(marketData).length > 0) {
    const coins = Object.values(marketData).filter(c => !c._meta);
    totalMarketCap = coins.reduce((sum, c) => sum + (c.marketCap || 0), 0);
    totalVolume = coins.reduce((sum, c) => sum + (c.volume24h || 0), 0);
  }
  
  return {
    marketCap: {
      value: formatCurrency(totalMarketCap),
      rawValue: totalMarketCap,
      change: { value: '+2.4%', isPositive: true },
      label: 'Market Cap',
    },
    volume24h: {
      value: formatCurrency(totalVolume),
      rawValue: totalVolume,
      change: { value: '+15.8%', isPositive: true },
      label: '24h Volume',
    },
    btcDominance: {
      value: `${stats.btcDominance}%`,
      rawValue: stats.btcDominance,
      change: { value: '-0.3%', isPositive: false },
      label: 'BTC Dominance',
    },
    fearGreed: {
      value: stats.fearGreedValue.toString(),
      rawValue: stats.fearGreedValue,
      change: { value: stats.fearGreedLabel, isPositive: true },
      label: 'Fear & Greed',
    },
  };
}

export default useLiveMarketData;
